import { createRouter, createWebHistory } from 'vue-router'

import { Permission } from '@/enums/permission'
import { StaffModule } from '@/enums/staff'
import { isAuthenticated } from '@/libs/auth'
import { useAppStore } from '@/stores/app'

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    scrollBehavior(to, from, savedPosition) {
        if (savedPosition)         return savedPosition
        if (to.hash)               return { el: to.hash }
        if (from.path === to.path) return {}

        return { top: 0, behavior: 'smooth' }
    },
    routes: [
        {
            // The presentation server's one-click sign-in (Logic/Demo.go, DemoSignIn.vue).
            path:      '/demo/:role',
            name:      'demo-sign-in',
            component: () => import('@/views/DemoSignIn.vue'),
            meta:      { layout: 'Blank', requiresAuth: false },
        },
        {
            path:      '/login',
            name:      'login',
            component: () => import('@/views/Login.vue'),
            meta:      { layout: 'Blank', requiresAuth: false },
        },
        {
            // Where a housekeeper lands: the rooms they have to do today. It is the
            // root path rather than /today because they open a bookmark, not a menu.
            path:      '/',
            name:      'today',
            component: () => import('@/views/housekeeping/Today.vue'),
            meta:      { requiresAuth: true },
        },
        {
            // The corridor tablet. NO session, and that is the whole design: it is a
            // device like the dining tablet and the room television, holding a key it
            // was given when it was paired (docs/OSEBJE.md §8). An account left signed
            // in on a wall for a year is a different and much worse thing than a key
            // one press revokes.
            //
            // Its own layout for the same reason: the Default layout's tab strip would
            // offer somebody's round to whoever walks past.
            path:      '/kiosk',
            name:      'kiosk',
            component: () => import('@/views/kiosk/Kiosk.vue'),
            meta:      { layout: 'Kiosk', requiresAuth: false },
        },
        {
            // One's own clock, in the portal she already has open (§8). No permission:
            // it is self-service, and the API narrows every answer to the signed-in
            // account, which is the guard.
            //
            // The MODULE is on the route and not only on the tab, exactly as "Moj
            // urnik" carries it: hiding the tab stops nobody from typing the address,
            // and the first `staff` call in a house without the licence answers 403
            // with code 19 — which locks the whole portal behind ModuleNotice. The
            // guard sends them home instead.
            path:      '/my-time',
            name:      'my-time',
            component: () => import('@/views/staff/MyTime.vue'),
            meta:      { requiresAuth: true, module: StaffModule.Staff },
        },
        {
            path:      '/rooms/:id',
            name:      'task',
            component: () => import('@/views/housekeeping/Task.vue'),
            meta:      { requiresAuth: true },
        },
        {
            // The supervisor's board: the whole day rather than one person's round.
            // The permission is on the route so the screen cannot be reached by typing
            // the address either — and it is the same permission the API guards
            // assign and generate with, so nothing here can be opened and then refused.
            path:      '/sheet',
            name:      'sheet',
            component: () => import('@/views/housekeeping/Sheet.vue'),
            meta:      { requiresAuth: true, permission: Permission.HousekeepingInspect },
        },
        {
            // The pairs of the morning, on their own screen rather than in a panel of
            // the plan dialog (v1.410.0). Who is in a team, when those two are actually
            // together, how many rooms they have left — questions asked all morning,
            // which is not where the window that rewrites the round belongs.
            //
            // Behind inspect, like the sheet and for the same reason: forming a team is
            // dividing the morning, and it is the permission the API guards every team
            // route with, reading included.
            path:      '/teams',
            name:      'teams',
            component: () => import('@/views/housekeeping/Teams.vue'),
            meta:      { requiresAuth: true, permission: Permission.HousekeepingInspect },
        },
        {
            // The whole house on one screen: every room's status, with today's work as
            // a badge on it. The supervisor's other question — the sheet says who has
            // the work, this says what state the building is in.
            path:      '/board',
            name:      'board',
            component: () => import('@/views/housekeeping/Board.vue'),
            meta:      { requiresAuth: true, permission: Permission.HousekeepingInspect },
        },
        {
            // The timeline folded into the board (v1.150.2); a bookmarked address
            // lands where the content went rather than on a missing page.
            path:     '/timeline',
            redirect: '/board',
        },
        {
            // How the last week or month went. Behind the same permission as the sheet
            // and for the same reason — it is the supervisor's own screen about her own
            // floor, and the API guards it with inspect rather than with report:read.
            path:      '/statistics',
            name:      'statistics',
            component: () => import('@/views/housekeeping/Statistics.vue'),
            meta:      { requiresAuth: true, permission: Permission.HousekeepingInspect },
        },
        {
            // The store cupboard, on the phone that pushes the trolley: what is on it,
            // what the house is short of, and moving something — to the laundry, onto a
            // bed, into a guest's bill.
            path:      '/stock',
            name:      'stock',
            component: () => import('@/views/stock/Stock.vue'),
            meta:      { requiresAuth: true, permission: Permission.HousekeepingRead },
        },
        {
            // What guests leave behind. A housekeeper writes it down here; everything
            // afterwards — the telephone call, the handover — happens at the desk, on
            // the reception portal, because a watch must not be signable-out from a
            // phone in a corridor.
            path:      '/lost-items',
            name:      'lost-items',
            component: () => import('@/views/lost/LostItems.vue'),
            meta:      { requiresAuth: true, permission: Permission.HousekeepingRead },
        },
        {
            // Her own week (docs/OSEBJE.md §7). Not behind a permission, and that is
            // not an omission: §13 makes reading your OWN rota the other side of being
            // in the record rather than a right somebody grants, so the API guards it
            // by narrowing the answer to the signed-in account and there is no
            // permission for a screen to check.
            //
            // What it IS behind is a licence — the `staff` module, which this portal
            // may or may not have beside its own `operations` — and that gate is the
            // guard below, not a `meta.permission`. Reaching this screen in a house
            // without it would fire a 403 with code 19 and lock the whole portal
            // (stores/app.ts).
            path:      '/my-rota',
            name:      'my-rota',
            component: () => import('@/views/staff/MyRota.vue'),
            meta:      { requiresAuth: true, module: StaffModule.Staff },
        },
        {
            // Everything broken in the house, on the screen the person who mends it
            // already carries. Behind its own permission, so a housekeeper does not get
            // a tab she cannot use and a handyman needs nothing else to work.
            path:      '/maintenance',
            name:      'maintenance',
            component: () => import('@/views/maintenance/Maintenance.vue'),
            meta:      { requiresAuth: true, permission: Permission.MaintenanceRead },
        },
    ],
})

/**
 * Where an account belongs when it opens the portal, in the order the question is asked.
 *
 * A handyman has no rooms to clean, so the day's list would greet him with an empty
 * screen and no way out of it; his own screen is the one he is sent to, including when he
 * types the address of one he may not open. „Danes“ is everybody else's and asks for no
 * permission, which is why it is LAST — a candidate that always holds has to be, or
 * nothing below it is ever reached.
 *
 * It is a list rather than the `if` that stood here until 2026-09-20 for the reason the
 * head-office console learned the expensive way: a landing that is worked out in a couple
 * of branches is a landing nobody reads as an ORDER, and the third branch somebody adds
 * goes in wherever it was typed. `pms-group-web/src/libs/navigation.ts` has the full
 * account of what that cost there.
 */
const HOME_CANDIDATES: { name: string, holds: (can: (key: string) => boolean) => boolean }[] = [
    { name: 'maintenance', holds: can => !can(Permission.HousekeepingRead) && can(Permission.MaintenanceRead) },
    { name: 'today',       holds: () => true },
]

/**
 * Whether a route name may be LANDED on at all.
 *
 * A path with a parameter in it cannot be addressed without a record — `{ name }` alone
 * throws „Missing required param“, the navigation never completes, and the person is
 * stuck on a redirect with no way into the portal. Neither name above is such a route;
 * this is the mechanical lock for the day one is, and it costs one `includes` per sign-in.
 */
const isLandable = (name: string): boolean => {
    const route = router.getRoutes().find(candidate => candidate.name === name)

    return !!route && !route.path.includes(':')
}

/** The first home this account may open, or nothing — see above. */
const homeRouteName = (can: (key: string) => boolean): string =>
    HOME_CANDIDATES.find(candidate => candidate.holds(can) && isLandable(candidate.name))?.name ?? ''

router.beforeEach(async (to) => {
    const appStore = useAppStore()
    const loggedIn = isAuthenticated()

    if (loggedIn && to.name === 'login') return { name: 'today' }

    if (to.meta.requiresAuth && !loggedIn) {
        appStore.setLogout()

        return { name: 'login' }
    }

    if (!to.meta.requiresAuth || !loggedIn) return true

    // Imported here rather than at the top of the file on purpose: the session helper
    // reaches the fetch store, which reaches this router, and a static cycle would
    // leave one of the three undefined at evaluation time depending on which module
    // the bundler happened to start with.
    const { loadSession } = await import('@/libs/session')

    await loadSession()

    // Where this account belongs — see HOME_CANDIDATES. Nothing is a possible answer and
    // means „send them nowhere“: a redirect that cannot be addressed is worse than a
    // screen that has little on it, so every `return` below asks for a home first.
    const home = homeRouteName(key => appStore.can(key))

    if (home && to.name === 'today' && home !== 'today') return { name: home }

    const required = to.meta.permission as string | undefined

    if (required && !appStore.can(required) && home && home !== to.name) return { name: home }

    // The licence, alongside the permission. A screen belonging to a module the house
    // has not bought is not merely empty here — its first call comes back 403 with
    // code 19, and stores/fetch.ts turns that into the notice App.vue draws over
    // everything (libs/license.ts). A typed address must therefore be turned away
    // before the screen loads, exactly as an unheld permission is.
    const module = to.meta.module as string | undefined

    if (module && !appStore.hasModule(module) && home && home !== to.name) return { name: home }

    return true
})

/**
 * A screen whose code is no longer on the server.
 *
 * Every screen is loaded when it is first opened. Deploying a new version replaces
 * those files with differently named ones, so a browser tab that was open across a
 * deploy asks for a file that no longer exists — and the click does nothing at all.
 * No error, no message: the person presses "Objekti" and the screen stays where it is.
 *
 * The tab heals itself by loading the new version once, at the address the person was
 * going to. The flag is what keeps that from becoming a loop when the failure is
 * something else — a portal that reloads forever is worse than one that does nothing.
 */
const RELOAD_FLAG = 'pms:reloaded-for-chunk'

const isMissingChunk = (error: unknown): boolean => {
    const message = error instanceof Error ? error.message : String(error)

    return /dynamically imported module|Importing a module script failed|Failed to fetch/i.test(message)
}

router.onError((error, to) => {
    if (!isMissingChunk(error)) return

    if (sessionStorage.getItem(RELOAD_FLAG)) return

    sessionStorage.setItem(RELOAD_FLAG, '1')
    window.location.assign(to.fullPath)
})

// A navigation that worked is the proof the code is current, so the guard is cleared
// and the next deploy gets its one reload too.
router.afterEach(() => sessionStorage.removeItem(RELOAD_FLAG))

export default router
